import { defineCrestModule } from '@crest/platform-contracts/modules';
import { projectsProjectDetailLauncherCapability } from '@crest/module-projects/contracts/detail-launcher';
import { projectsProjectReferenceAccessHoldCapability } from '@crest/module-projects/contracts/project-reference';
import { platformOptionalFactProviderHostCapability } from '@crest/platform-contracts/hosts';
import { procurementCostSummariesCapability, procurementOrderNumberingCapability, procurementOrdersCapability, procurementOrderVersionsCapability, } from './contracts/procurement-order.contract.js';
import { procurementFulfillmentCapability } from './contracts/procurement-fulfillment.contract.js';
import { procurementImportCapability } from './contracts/procurement-import.contract.js';
import { procurementReceivingCapability } from './contracts/procurement-receiving.contract.js';
import { procurementSubcontractsCapability } from './contracts/procurement-subcontract.contract.js';
import { procurementFinanceProfitabilityCostsCapability, procurementFinanceProfitabilityCostsProviderId, } from './contracts/procurement-finance-profitability-costs.contract.js';
export const procurementModuleManifest = defineCrestModule({
    id: 'procurement',
    version: '1.0.0',
    kind: 'business',
    required: false,
    requires: ['platform', 'projects'],
    requiredCapabilities: [
        'platform.tenant-context.v1',
        'platform.module-access.v1',
        'platform.membership-directory.v1',
        'projects.detail-host.v1',
        projectsProjectDetailLauncherCapability,
        'projects.project-order-reference.v1',
        projectsProjectReferenceAccessHoldCapability,
        'projects.project-access-decision.v2',
        'platform.storage-object-reference.v1',
        platformOptionalFactProviderHostCapability,
    ],
    optionalCapabilities: [
        'relationships.organizations.v1',
        'relationships.contacts.v1',
        'estimating.pricebook.v1',
        'documents.templates.v1',
        'communications.email-delivery.v1',
    ],
    optionalCapabilityFallbacks: [{
            capability: 'relationships.organizations.v1',
            degradedMode: 'procurement.degraded.counterparty-snapshot.v1',
        }, {
            capability: 'relationships.contacts.v1',
            degradedMode: 'procurement.degraded.counterparty-snapshot.v1',
        }, {
            capability: 'estimating.pricebook.v1',
            degradedMode: 'procurement.degraded.manual-order-lines.v1',
        }, {
            capability: 'documents.templates.v1',
            degradedMode: 'procurement.degraded.no-generated-document.v1',
        }, {
            capability: 'communications.email-delivery.v1',
            degradedMode: 'procurement.degraded.no-provider-delivery.v1',
        }],
    provides: [
        procurementOrdersCapability,
        procurementOrderVersionsCapability,
        procurementOrderNumberingCapability,
        procurementCostSummariesCapability,
        procurementSubcontractsCapability,
        procurementFulfillmentCapability,
        procurementReceivingCapability,
        procurementImportCapability,
        procurementFinanceProfitabilityCostsCapability,
    ],
    permissions: [
        'procurement.orders.read',
        'procurement.orders.manage',
        'procurement.orders.read-all',
        'procurement.costs.read',
        'procurement.subcontracts.read',
        'procurement.subcontracts.manage',
        'procurement.fulfillment.read',
        'procurement.fulfillment.manage',
        'procurement.receiving.read',
        'procurement.receiving.manage',
        'procurement.import.read',
        'procurement.import.execute',
        'procurement.import.rollback',
    ],
    settings: [
        'procurement.orders.configuration',
        'procurement.orders.display',
        'procurement.copy.orders',
        'procurement.supplier-operations.configuration',
        'procurement.supplier-operations.display',
        'procurement.copy.supplier-operations',
    ],
    copyCatalogs: ['procurement.copy.orders', 'procurement.copy.supplier-operations'],
    events: {
        publishes: [
            'procurement.order-created.v1',
            'procurement.order-revised.v1',
            'procurement.order-status-changed.v1',
            'procurement.order-archived.v1',
            'procurement.order-restored.v1',
            'procurement.subcontract-created.v1',
            'procurement.subcontract-revised.v1',
            'procurement.subcontract-archived.v1',
            'procurement.subcontract-restored.v1',
            'procurement.fulfillment-created.v1',
            'procurement.fulfillment-revised.v1',
            'procurement.fulfillment-event-recorded.v1',
            'procurement.fulfillment-archived.v1',
            'procurement.fulfillment-restored.v1',
            'procurement.receiving-created.v1',
            'procurement.receiving-revised.v1',
            'procurement.receiving-archived.v1',
            'procurement.receiving-restored.v1',
        ],
        subscribes: [],
    },
    server: {
        routes: [
            { id: 'procurement.routes.orders', priority: 0 },
            { id: 'procurement.routes.subcontracts', priority: 10 },
            { id: 'procurement.routes.fulfillments', priority: 20 },
            { id: 'procurement.routes.receiving', priority: 30 },
        ],
        jobs: [],
        subscribers: [],
        slots: [{
                id: procurementFinanceProfitabilityCostsProviderId,
                requiredCapability: platformOptionalFactProviderHostCapability,
                requiredPermission: 'procurement.costs.read',
                priority: 0,
            }],
    },
    web: {
        routes: [
            { id: 'procurement.web.orders', priority: 0 },
            { id: 'procurement.web.supplier-operations', priority: 10 },
        ],
        navigation: [
            { id: 'procurement.navigation.orders', priority: 70 },
            { id: 'procurement.navigation.supplier-operations', priority: 71 },
        ],
        slots: [{
                id: 'procurement.web.projects.order-summary',
                requiredCapability: 'projects.detail-host.v1',
                requiredPermission: 'procurement.orders.read',
                priority: 50,
            }, {
                id: 'procurement.web.projects.supplier-operations-summary',
                requiredCapability: 'projects.detail-host.v1',
                requiredPermission: 'procurement.subcontracts.read',
                priority: 51,
            }],
    },
    mobile: { routes: [], slots: [] },
    portal: { routes: [], slots: [] },
    database: {
        schema: 'procurement',
        owns: [
            'procurement.orders',
            'procurement.order_versions',
            'procurement.order_version_items',
            'procurement.order_events',
            'procurement.order_commands',
            'procurement.order_number_counters',
            'procurement.reject_immutable_change',
            'procurement.current_membership_can_read_order',
            'procurement.current_membership_can_manage_order',
            'procurement.order_version_record',
            'procurement.order_record',
            'procurement.write_order_version',
            'procurement.count_orders',
            'procurement.get_order_command_receipt',
            'procurement.execute_order_command',
            'procurement.list_orders',
            'procurement.get_order',
            'procurement.list_order_versions',
            'procurement.read_project_cost_summary',
            'procurement.supplier_records',
            'procurement.supplier_record_versions',
            'procurement.fulfillment_events',
            'procurement.supplier_record_events',
            'procurement.supplier_record_commands',
            'procurement.supplier_read_permission',
            'procurement.supplier_manage_permission',
            'procurement.current_membership_can_read_supplier_record',
            'procurement.current_membership_can_manage_supplier_record',
            'procurement.validate_receiving_object_references',
            'procurement.write_supplier_record_version',
            'procurement.supplier_record_json',
            'procurement.get_supplier_record_command_receipt',
            'procurement.get_supplier_record',
            'procurement.count_supplier_records',
            'procurement.list_supplier_records',
            'procurement.sync_fulfillment_from_receiving',
            'procurement.execute_supplier_record_command',
            'procurement.import_domain_totals_valid',
            'procurement.import_runs',
            'procurement.import_identity_map',
            'procurement.import_row_receipts',
            'procurement.import_rejects',
            'procurement.import_rollback_receipts',
            'procurement.begin_import_run',
            'procurement.record_import_reject',
            'procurement.record_import_receipt',
            'procurement.complete_import_run',
            'procurement.fail_import_run',
            'procurement.mark_import_run_rolled_back',
            'procurement.get_import_run',
            'procurement.get_import_run_evidence',
        ],
        readsThrough: [
            'platform.membership-directory.v1',
            'projects.project-order-reference.v1',
            projectsProjectReferenceAccessHoldCapability,
            'projects.project-access-decision.v2',
            'platform.storage-object-reference.v1',
        ],
        migrations: [
            'procurement.order-foundation',
            'procurement.subcontracts-fulfillment-receiving',
            'procurement.import-reconciliation',
            'procurement.project-reference-mutation-holds',
        ],
    },
    health: ['procurement.database'],
    lifecycle: {
        initialization: { timeoutMs: 5_000, failureBehavior: 'quarantine-module' },
        resources: {
            requestConcurrency: 96,
            jobConcurrency: 0,
            queueBacklogLimit: 0,
            databaseConnectionBudget: 12,
            workerIsolation: 'shared-api',
        },
        disable: {
            data: 'retain',
            clientContributions: 'remove',
            serverRoutes: 'deny-tenant-access',
            scheduledJobs: 'skip',
            events: {
                behavior: 'drop-safe',
                durableCursor: false,
                retentionDays: 0,
                backlogLimit: 0,
                warningAt: 0,
                reconciliationProcedure: null,
                dropSafeReason: 'The order foundation publishes facts but owns no delivery subscriber, provider, or worker.',
            },
            outboundSideEffects: 'suppress-no-retroactive-send',
            inFlight: 'finish-idempotently',
        },
        resume: {
            configuration: 'last-compatible-published',
            healthChecks: ['procurement.database'],
            reconciliationProcedure: null,
            visibility: 'after-health-and-reconciliation',
        },
        uninstall: {
            executableCode: 'remove',
            data: 'retain',
            tombstone: 'retain-immutable',
            destruction: 'separate-explicit-authority',
        },
    },
    retentionPolicy: 'Procurement domain history, cost facts, import identities, rejects, reconciliation, and replay/rollback receipts remain stored while disabled.',
});
//# sourceMappingURL=procurement.module.js.map